import { supabase } from "./supabase";
import { mockApi } from "./mock";
import type { Accent, AudioResolutionStatus } from "../../shared/types";

export class ApiError extends Error {
  constructor(message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (import.meta.env.VITE_DEMO_MODE === "true") return mockApi<T>(path, init);
  const { data: { session } } = await supabase.auth.getSession();
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (session?.access_token) headers.set("Authorization", `Bearer ${session.access_token}`);
  const response = await fetch(path, { ...init, headers });
  const payload = response.headers.get("content-type")?.includes("application/json")
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const message = typeof payload === "object" && payload && "error" in payload
      ? String((payload as { error: unknown }).error)
      : `Yêu cầu thất bại (${response.status}).`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

export const json = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});

export async function createAudioResolution(body: { scope: "import_batch" | "all" | "item"; accent: Accent; import_batch_id?: string; item_id?: string }) {
  return api<AudioResolutionStatus & { run_id: string }>("/api/audio/resolutions", json("POST", body));
}

export async function processAudioResolution(runId: string, limit = 3) {
  return api<AudioResolutionStatus>(`/api/audio/resolutions/${runId}/process?limit=${limit}`, json("POST"));
}

export async function getAudioResolution(runId: string) {
  return api<AudioResolutionStatus>(`/api/audio/resolutions/${runId}`);
}

export async function resolveItemAudio(itemId: string, accent: Accent) {
  return api<AudioResolutionStatus>(`/api/items/${itemId}/audio/${accent}/resolve`, json("POST"));
}

export async function downloadExport() {
  const payload = await api<Record<string, unknown>>("/api/export");
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `kungkung-export-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
